import React from 'react'
import { Box, Skeleton, styled } from '@mui/material'
import { Topic } from '@/types'

const Rail = styled(Box)({
  display: 'flex',
  gap: 14,
  overflowX: 'auto',
  padding: '4px 4px 14px',
  scrollSnapType: 'x proximity',
  margin: '0 -4px',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { height: 6 },
  '&::-webkit-scrollbar-thumb': { background: 'var(--ds-border-strong)', borderRadius: 6 },
})

const Card = styled(Box)({
  flex: '0 0 250px',
  scrollSnapAlign: 'start',
  position: 'relative',
  aspectRatio: '4/3',
  borderRadius: 'var(--ds-radius-lg)',
  overflow: 'hidden',
  boxShadow: 'var(--ds-shadow-soft)',
  background: 'var(--ds-night-canvas)',
  cursor: 'pointer',
  transition: 'transform 260ms cubic-bezier(0.16,1,0.3,1), box-shadow 260ms ease',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: 'var(--ds-shadow-lift)' },
  '&:hover .rail-cov': { transform: 'scale(1.07)' },
})

const Cov = styled('div')({
  position: 'absolute',
  inset: 0,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'transform 700ms cubic-bezier(0.16,1,0.3,1)',
})

const CardScrim = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: `linear-gradient(180deg,
    color-mix(in srgb, var(--ds-ink) 0%, transparent) 45%,
    color-mix(in srgb, var(--ds-ink) 88%, transparent) 100%)`,
})

const Badge = styled(Box)({
  position: 'absolute',
  top: 9,
  left: 9,
  zIndex: 2,
  height: 22,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '0 9px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'color-mix(in srgb, var(--ds-ink) 65%, transparent)',
  backdropFilter: 'blur(8px)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  color: 'var(--ds-accent-dot-1)',
})

const Meta = styled(Box)({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2,
  padding: '12px 14px 13px',
  color: 'var(--ds-night-ink)',
})

const Title = styled('div')({
  fontSize: 14,
  fontWeight: 680,
  letterSpacing: '-0.015em',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const Sub = styled('div')({
  fontSize: 10.5,
  color: 'color-mix(in srgb, var(--ds-night-ink) 70%, transparent)',
  marginTop: 3,
})

export interface GalleryRailProps {
  topics: Topic[]
  /** Resolved photo/room covers keyed by topicID — do not use topic.profilePicThumbURL (owner avatar). */
  coverById?: Record<string, string | undefined>
  loading?: boolean
  skeletonCount?: number
  onOpen?: (topic: Topic) => void
}

export function GalleryRail({ topics, coverById, loading, skeletonCount = 4, onOpen }: GalleryRailProps) {
  if (loading) {
    return (
      <Rail>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Skeleton key={i} variant='rectangular' sx={{ flex: '0 0 250px', aspectRatio: '4/3', borderRadius: 'var(--ds-radius-lg)' }} />
        ))}
      </Rail>
    )
  }

  if (!topics.length) return null

  return (
    <Rail>
      {topics.map((t) => {
        const n = parseInt(t.photoCount ?? '0', 10) || 0
        const printStr = `${n} ${n === 1 ? 'print' : 'prints'}`
        const subStr = [printStr, t.visibility === 'public' ? 'Public' : null].filter(Boolean).join(' · ')
        const cover = coverById?.[t.topicID]
        return (
          <Card key={t.topicID} onClick={() => onOpen?.(t)} role='button' tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onOpen?.(t)}>
            <Cov
              className='rail-cov'
              style={
                cover
                  ? { backgroundImage: `url(${cover})` }
                  : {
                      background: `
                        radial-gradient(ellipse 80% 70% at 30% 40%, color-mix(in srgb, var(--ds-accent-dot-3) 40%, transparent), transparent 60%),
                        linear-gradient(145deg, var(--ds-night-surface), var(--ds-night-canvas))
                      `,
                    }
              }
            />
            <Badge>◈ WALKABLE</Badge>
            <CardScrim />
            <Meta>
              <Title title={t.topicName}>{t.topicName}</Title>
              <Sub>{subStr}</Sub>
            </Meta>
          </Card>
        )
      })}
    </Rail>
  )
}
